// /app/upgrade — dedicated Pro upgrade page.
//
// Strategy: lead with the AI features the user can't get on Starter (Call
// Coach + Rehab Scan + Click-to-Call). Side-by-side comparison. Annual
// toggle with 20% discount. FAQ. Single CTA at the bottom.
//
// Honors the existing Stripe checkout flow via /api/billing/checkout.

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ShieldCheck, Sparkles, Phone, Headphones, Inbox, Brain,
  Check, X, Star, ArrowRight, Crown, Loader2, ChevronDown,
} from "lucide-react";

const FAQ = [
  {
    q: "What happens to my existing data when I upgrade?",
    a: "Nothing changes. Your leads, pipeline, contacts, contracts, recordings — all stay exactly where they are. You just unlock the Pro-only features instantly.",
  },
  {
    q: "Can I cancel any time?",
    a: "Yes. Cancel inside the app any time and you'll keep Pro access through the end of your billing period. No phone calls, no retention pitch.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — 14 days, no credit card commitment beyond what Stripe takes for the trial. Cancel inside the trial window and you're never charged.",
  },
  {
    q: "What's the difference between Quick Estimate and AI Rehab Scan?",
    a: "Quick Estimate (free on every tier) lets you pick a tier — Light/Medium/Heavy/Gut — and get a $/sqft-based ballpark in 2 seconds. AI Rehab Scan (Pro only) lets you upload phone photos; Claude reads them, identifies condition system-by-system, and produces a verified scope of work with a buyer-ready badge on your shared report.",
  },
  {
    q: "How does the AI Call Coach work?",
    a: "Every call you place through the in-browser dialer is auto-recorded. Within 60 seconds of hangup, Whisper transcribes the call and Claude grades you across 4 pillars: Rapport, Discovery, Objection Handling, Close. You get a 0-100 score, a one-paragraph summary, the seller's real objection, and a 'next-best line' you can copy-paste into your follow-up.",
  },
  {
    q: "Do I need my own Twilio account to use SMS + calling?",
    a: "No — that's optional. Pro tier lets you buy iSpeedToClose-managed numbers directly inside the app for $5/mo each (search by area code, ready in 30 seconds, no Twilio setup or A2P paperwork). If you already have a Twilio account, you can connect it instead — both paths work side-by-side.",
  },
  {
    q: "What about TCPA / Do-Not-Call compliance?",
    a: "Pro tier ships with a built-in DNC guard. Every outbound SMS and voice call is screened against the National Do-Not-Call registry BEFORE it sends. Blocked sends get an immutable audit row. If you have prior express written consent, override with one click — the attestation is timestamped for legal defense. We're the only wholesaler CRM that ships this protection out of the box.",
  },
  {
    q: "What if I'm grandfathered at an older price?",
    a: "If you signed up on a legacy tier (e.g. old Solo at $79/mo or original Pro at $99/mo), you stay at that price forever. Only new signups see the current Professional tier price of $197/mo.",
  },
];

const PRO_HEROES = [
  { icon: Brain, title: "AI Call Coach", desc: "Whisper + Claude score every seller call out of 100 in under 60 seconds. With summary, true objection, and next-best-line." },
  { icon: ShieldCheck, title: "TCPA-Safe Outbound", desc: "Every SMS + voice call screened against the National DNC registry before sending. Audit-logged consent overrides for legal defense — no $1,500/violation surprises." },
  { icon: Phone, title: "Managed Phone Numbers · $5/mo", desc: "Pick a US local number directly inside iSpeedToClose. No Twilio account setup. No A2P paperwork. Search by area code, ready in 30 seconds." },
  { icon: Phone, title: "Click-to-Call + Always-On Recording", desc: "In-browser dialer. Every call auto-attributed to the lead, transcribed, and scored. No paperwork, no separate phone." },
  { icon: ShieldCheck, title: "AI Rehab Scan", desc: "Drop 5-15 photos. Claude scopes the rehab — tier, range, per-system breakdown, written scope of work. AI-verified badge auto-attaches to shared reports." },
  { icon: Inbox, title: "AI Deals Inbox", desc: "Curated off-market deal feed scored against your buy-box. Wake up to deals already underwritten." },
];

const COMPARE_ROWS = [
  { feature: "Active leads", starter: "25", pro: "Unlimited", hero: false },
  { feature: "AI Deal Analyzer", starter: "5 / month", pro: "Unlimited", hero: false },
  { feature: "Discover searches", starter: "10 / day", pro: "Unlimited", hero: false },
  { feature: "AI Call Coach (Whisper + Claude grading)", starter: false, pro: true, hero: true },
  { feature: "AI Rehab Scan (Claude Vision)", starter: false, pro: "5 / day", hero: true },
  { feature: "Click-to-Call + Always-On Recording", starter: false, pro: true, hero: true },
  { feature: "iSpeedToClose-managed phone numbers ($5/mo)", starter: false, pro: true, hero: true },
  { feature: "TCPA-safe outbound · DNC guard + audit trail", starter: false, pro: true, hero: true },
  { feature: "AI Coach Weekly Digest", starter: false, pro: true, hero: true },
  { feature: "Deals Inbox (AI-vetted)", starter: false, pro: true, hero: true },
  { feature: "AI-Verified badge on shared reports", starter: false, pro: true, hero: true },
  { feature: "Instant GO/MAYBE email alerts", starter: false, pro: true, hero: false },
  { feature: "Saved searches", starter: "1", pro: "10", hero: false },
  { feature: "Capture forms", starter: "1", pro: "3", hero: false },
  { feature: "Email drip campaigns", starter: "1", pro: "3 + AI personalize", hero: false },
  { feature: "Shared report watermark", starter: "Visible", pro: "Removed", hero: false },
  { feature: "Power Team contacts", starter: "10", pro: "Unlimited", hero: false },
  { feature: "Email support", starter: "Community only", pro: true, hero: false },
];

export default function UpgradeToPro() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [annual, setAnnual] = useState(true);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const fromFeature = search.get("from"); // optional context — e.g. ?from=ai_scan

  useEffect(() => {
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const isAlreadyPro = status?.tier && status.tier !== "free";

  const checkout = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/checkout", {
        tier: "pro",
        billing_cycle: annual ? "annual" : "monthly",
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10" data-testid="upgrade-page">
      {/* HERO */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-3 py-1 mb-5">
          <Crown size={12} /> Upgrade to Pro
        </div>
        <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl mb-4 text-[var(--ink)]">
          The <span className="text-[var(--primary)]">AI co-pilot</span><br />for every wholesale deal.
        </h1>
        <p className="text-lg text-[var(--ink-2)] max-w-2xl mx-auto leading-relaxed">
          Starter gets you the CRM. <b className="text-[var(--ink)]">Pro</b> gets you an AI that grades every call,
          scopes every rehab, and stamps every shared report with verified credibility.
        </p>
        {fromFeature && (
          <div className="mt-4 inline-block text-sm bg-[var(--primary)]/10 border border-[var(--primary)]/30 text-[var(--primary-dark)] px-4 py-2 rounded-full">
            <Sparkles size={12} className="inline mr-1.5 -mt-[2px]" />
            You hit a Pro feature — upgrade to unlock it instantly.
          </div>
        )}
      </div>

      {/* HERO FEATURE GRID */}
      <div className="grid sm:grid-cols-2 gap-4 mb-12">
        {PRO_HEROES.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="card flex gap-4 items-start" data-testid={`pro-hero-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <div className="w-10 h-10 rounded-xl bg-[var(--primary)]/10 text-[var(--primary-dark)] flex items-center justify-center flex-shrink-0">
              <Icon size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-base text-[var(--ink)]">{title}</div>
              <div className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">{desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* PRICING + ANNUAL TOGGLE */}
      <div className="card max-w-md mx-auto text-center mb-10 relative overflow-hidden" data-testid="upgrade-pricing-card">
        <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--primary)]" />
        <div className="label-eyebrow flex items-center justify-center gap-1.5 mb-2">
          <Star size={11} className="text-[var(--primary)]" /> Professional tier
        </div>
        <div className="flex items-baseline justify-center gap-2 mb-1">
          <span className="font-display font-black text-6xl tracking-tighter text-[var(--ink)]">
            ${annual ? "164" : "197"}
          </span>
          <span className="text-[var(--ink-2)] text-sm">/ month</span>
        </div>
        <div className="text-xs text-[var(--ink-3)] mb-5">
          {annual ? <>billed annually — <b className="text-[var(--ink)]">$1,970/yr</b> · 2 months free</> :
                    <>billed monthly — switch to annual to save 2 months</>}
        </div>
        <ToggleBilling annual={annual} onChange={setAnnual} />
        <button
          onClick={isAlreadyPro ? () => nav("/app/billing") : checkout}
          disabled={loading}
          className="btn-money w-full mt-5 text-base !py-3.5 disabled:opacity-60"
          data-testid="upgrade-checkout-cta"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> :
           isAlreadyPro ? <>Manage billing <ArrowRight size={14} /></> :
           <>Start 14-day free trial <ArrowRight size={14} /></>}
        </button>
        <div className="text-[11px] text-[var(--ink-3)] mt-3">
          {isAlreadyPro ? "You're already on Pro. Manage your plan →" :
            "No credit card commitment beyond Stripe's trial hold. Cancel any time."}
        </div>
      </div>

      {/* COMPARISON TABLE */}
      <div className="card overflow-hidden mb-12 p-0">
        <div className="text-center py-6 border-b border-[var(--line)]">
          <h2 className="font-display font-bold text-2xl text-[var(--ink)]">Starter vs. Pro — full breakdown</h2>
        </div>
        <table className="w-full" data-testid="upgrade-compare-table">
          <thead>
            <tr className="bg-[var(--bg)] border-b border-[var(--line)]">
              <th className="text-left px-5 py-3 text-xs uppercase tracking-widest font-bold text-[var(--ink-3)]">Feature</th>
              <th className="text-center px-5 py-3 text-xs uppercase tracking-widest font-bold text-[var(--ink-3)]">Starter</th>
              <th className="text-center px-5 py-3 text-xs uppercase tracking-widest font-bold text-[var(--primary-dark)]">Pro</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row, i) => (
              <tr key={i} className={`border-b border-[var(--line)] ${row.hero ? "bg-[var(--primary)]/[0.04]" : ""}`}>
                <td className="px-5 py-3 text-sm">
                  <span className={row.hero ? "font-bold text-[var(--ink)]" : "text-[var(--ink)]"}>{row.feature}</span>
                  {row.hero && <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-[var(--primary-dark)] bg-[var(--primary)]/15 px-1.5 py-[1px] rounded">PRO</span>}
                </td>
                <td className="text-center px-5 py-3 text-sm text-[var(--ink-2)]">
                  <Cell v={row.starter} />
                </td>
                <td className="text-center px-5 py-3 text-sm text-[var(--ink)] font-semibold">
                  <Cell v={row.pro} pro />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SOCIAL PROOF */}
      <div className="card text-center py-10 mb-12 bg-gradient-to-br from-[var(--primary)]/[0.04] to-white">
        <div className="flex justify-center gap-1 mb-5">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={18} className="text-[var(--primary)]" fill="currentColor" />)}
        </div>
        <blockquote className="font-display font-bold text-xl sm:text-2xl leading-[1.3] tracking-tight text-[var(--ink)] max-w-2xl mx-auto px-6">
          &ldquo;The AI Call Coach alone is worth Pro. It caught two objections in one week I would&apos;ve missed —
          both deals closed.&rdquo;
        </blockquote>
        <div className="mt-5 text-sm text-[var(--ink-2)]">
          <span className="font-bold text-[var(--ink)]">Real operator</span> · 47 closes · Phoenix wholesaler
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto mb-12">
        <h2 className="font-display font-bold text-2xl text-center mb-6 text-[var(--ink)]">Quick FAQ</h2>
        <div className="divide-y divide-[var(--line)] border border-[var(--line)] rounded-2xl bg-white">
          {FAQ.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              className="w-full text-left px-5 py-4 hover:bg-[var(--bg)] transition-colors"
              data-testid={`upgrade-faq-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-semibold text-[var(--ink)] text-sm">{f.q}</span>
                <ChevronDown size={16} className={`text-[var(--ink-3)] flex-shrink-0 mt-0.5 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </div>
              {openFaq === i && (
                <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{f.a}</p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* FINAL CTA */}
      <div className="text-center mb-8">
        <button
          onClick={isAlreadyPro ? () => nav("/app/billing") : checkout}
          disabled={loading}
          className="btn-money !text-base !py-3.5 !px-7"
          data-testid="upgrade-final-cta"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> :
           isAlreadyPro ? "Manage billing" :
           <>Start 14-day free trial <ArrowRight size={14} /></>}
        </button>
        <p className="text-xs text-[var(--ink-3)] mt-3">
          14-day free trial · cancel any time · no surprise charges
        </p>
      </div>
    </div>
  );
}

function ToggleBilling({ annual, onChange }) {
  return (
    <div className="inline-flex items-center bg-[var(--bg)] rounded-full p-1 border border-[var(--line)]" data-testid="upgrade-billing-toggle">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors ${!annual ? "bg-white text-[var(--ink)] shadow-sm" : "text-[var(--ink-3)]"}`}
        data-testid="upgrade-toggle-monthly"
      >
        Monthly
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors flex items-center gap-1.5 ${annual ? "bg-white text-[var(--ink)] shadow-sm" : "text-[var(--ink-3)]"}`}
        data-testid="upgrade-toggle-annual"
      >
        Annual
        <span className="text-[9px] font-black uppercase tracking-widest text-[var(--primary-dark)] bg-[var(--primary)]/15 px-1.5 py-[1px] rounded">−20%</span>
      </button>
    </div>
  );
}

function Cell({ v, pro = false }) {
  if (v === true) {
    return <Check size={16} className={`inline ${pro ? "text-[var(--primary-dark)]" : "text-emerald-600"}`} />;
  }
  if (v === false) {
    return <X size={14} className="inline text-[var(--ink-3)]" />;
  }
  return <span>{v}</span>;
}
